/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo } from 'react';
import { Form, Input, Select, Button, Checkbox } from 'antd';
import { Segment, SegmentGroup, getCountry } from '@fxtr/i18n';
import config from '$config';
import { isRenault } from '$app/brand';
import { AddressSelectorWithGooglePlaces } from '$app/shared/components/AddressSelectorWithGooglePlaces';
import { useMaiaT } from '$app/i18n/useMaiaT';
import {
  createPhoneBlurHandler,
  getPhoneFieldRules,
  getPhonePlaceholder,
  GARAGE_PHONE_FIELD_NAMES,
  revalidatePhoneFields,
} from '../../../utils/phoneValidation';
import { businessNameHelpKey } from '../../../utils/businessNameHelpKey';
import { shouldShowTresorId } from '../../../utils/shouldShowTresorId';
import { isTresorIdReadOnly } from '../../../utils/isTresorIdReadOnly';
import { TRESOR_ID_MAX_LENGTH } from '../../../utils/normalizeOptionalStrings';
import GarageWebpageLink from '../GarageWebpageLink';
import {
  FormContainer,
  FormColumn,
  FormGroupTite,
  FormItem,
  HR,
  LookupContainer,
  AddressContainer,
} from './styled';

const { Option } = Select;
const isMotrio = config.segmentGroup === 'MOTRIO';
// Only FIXTER garages appear on invoices; partner groups (MOTRIO, future ones) don't.
const isFixter = config.segmentGroup === SegmentGroup.FIXTER;
const businessNameHelpDefaults = {
  'maia.garageInfo.help.businessNameMotrioWebsite': 'This is what will be displayed on the MOTRIO website',
  'maia.garageInfo.help.businessNameRenaultWebsite': 'This is what will be displayed on the Renault website',
  'maia.garageInfo.help.businessName':
    'DO NOT ADD NOTES HERE: The name here will be used on the website and invoices.',
  'maia.garageInfo.help.businessNameNoInvoices': 'DO NOT ADD NOTES HERE: The name here will be used on the website.',
};

const visibleGaragePhoneFieldNames = isMotrio ? ['phoneNumber', 'contactPhone'] : GARAGE_PHONE_FIELD_NAMES;

function GarageForm(props) {
  const t = useMaiaT();
  const { loading, canEditPremiumMembership, form, initialValue: garage, lockedSegment } = props;
  const { getFieldDecorator } = form;

  const showSegment =
    config.env !== 'production' ||
    document.cookie.split(';').some((cookie) => cookie.trim().startsWith('motrioCheckoutAccess='));

  // The segment is locked (read-only) when it's inherited from the top-bar selection
  // (partner groups, on create) or when editing an existing garage, whose segment must
  // not change after creation.
  const isExistingGarage = Boolean(garage?.segment);
  const isSegmentLocked = Boolean(lockedSegment) || isExistingGarage;

  // Fixter segments are always selectable; the rest are gated behind `showSegment`, but
  // the currently selected segment (inherited or the existing garage's) must always
  // render so its label displays.
  const visibleSegments = [lockedSegment, garage?.segment].filter(Boolean);
  const segmentOptions = [
    { value: Segment.Fixter_UK, label: t('maia.garageInfo.segment.fixterUk', 'United Kingdom 🇬🇧'), gated: false },
    { value: Segment.Fixter_FR, label: t('maia.garageInfo.segment.fixterFr', 'France 🇫🇷'), gated: false },
    { value: Segment.Motrio_UK, label: t('maia.garageInfo.segment.motrioUk', 'Motrio UK 🇬🇧'), gated: true },
    { value: Segment.Motrio_FR, label: t('maia.garageInfo.segment.motrioFr', 'Motrio France 🇫🇷'), gated: true },
    { value: Segment.Motrio_IT, label: t('maia.garageInfo.segment.motrioIt', 'Motrio Italy 🇮🇹'), gated: true },
    { value: Segment.Motrio_ES, label: t('maia.garageInfo.segment.motrioEs', 'Motrio Spain 🇪🇸'), gated: true },
    { value: Segment.Motrio_PT, label: t('maia.garageInfo.segment.motrioPt', 'Motrio Portugal 🇵🇹'), gated: true },
    { value: Segment.Motrio_NL, label: t('maia.garageInfo.segment.motrioNl', 'Motrio Netherlands 🇳🇱'), gated: true },
    { value: Segment.Motrio_RO, label: t('maia.garageInfo.segment.motrioRo', 'Motrio Romania 🇷🇴'), gated: true },
    { value: Segment.Motrio_PL, label: t('maia.garageInfo.segment.motrioPl', 'Motrio Poland 🇵🇱'), gated: true },
    { value: Segment.Renault_MA, label: t('maia.garageInfo.segment.renaultMa', 'Renault Morocco 🇲🇦'), gated: true },
  ].filter(
    (option) => option.value != null && (!option.gated || showSegment || visibleSegments.includes(option.value))
  );

  // `initialValue` only seeds the field on mount, so keep the locked field in sync when
  // the inherited segment changes (e.g. the user switches the top-bar selection). The
  // value guard prevents a redundant write (and re-render loop) once it already matches.
  useEffect(() => {
    if (lockedSegment && form.getFieldValue('segment') !== lockedSegment) {
      form.setFieldsValue({ segment: lockedSegment });
      revalidatePhoneFields(form, visibleGaragePhoneFieldNames);
    }
  }, [lockedSegment, form]);

  const setAddressValues = useCallback(
    (address) => {
      if (!address.line1 && address.line2) {
        form.setFields({
          addressLine1: {
            value: address.line2,
          },
        });
      } else {
        form.setFields({
          addressLine1: {
            value: address.line1,
          },
        });
      }

      form.setFields({
        addressLine2: { value: address.line2 },
        city: { value: address.city },
        county: { value: address.city },
        postcode: { value: address.postcode },
        latitude: { value: address.latitude },
        longitude: { value: address.longitude },
      });
    },
    [form]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    props.onSubmit(e, form);
  };

  const handleSegmentChange = useCallback(() => {
    revalidatePhoneFields(form, visibleGaragePhoneFieldNames);
  }, [form]);

  const phoneHelp = t(
    'maia.garageInfo.help.phoneNumber',
    'Include country code or use a local number. Values are saved in international format.'
  );
  const contactHelp = t(
    'maia.garageInfo.help.contactNumber',
    'Include country code or use a local number. Values are saved in international format.'
  )
  const activeSegment = form.getFieldValue('segment') || lockedSegment || garage?.segment;
  // Avoid resetting the Autocomplete restriction on every render.
  const googlePlacesCountries = useMemo(() => {
    if (!activeSegment) return undefined;
    return [getCountry(activeSegment).toLowerCase()];
  }, [activeSegment]);
  const phonePlaceholder = getPhonePlaceholder(activeSegment, t);

  const showTresorId = shouldShowTresorId(activeSegment);
  const tresorIdReadOnly = isTresorIdReadOnly(garage);
  const businessNameHelpKeyForSegment = businessNameHelpKey(activeSegment, isFixter);
  const businessNameHelp = t(businessNameHelpKeyForSegment, businessNameHelpDefaults[businessNameHelpKeyForSegment]);

  const renderPhoneInput = (fieldName, { mobileOnly = false } = {}) =>
    getFieldDecorator(fieldName, {
      rules: getPhoneFieldRules({ form, segment: activeSegment, mobileOnly, t }),
    })(
      <Input
        type="tel"
        placeholder={phonePlaceholder}
        onBlur={createPhoneBlurHandler({ form, fieldName, segment: activeSegment, mobileOnly })}
      />
    );

  return (
    <Form name="Garage Edit" layout="vertical" onSubmit={handleSubmit}>
      <FormContainer>
        <FormColumn>
          <FormGroupTite>{t('maia.garageInfo.section.businessInformation', 'Business information')}</FormGroupTite>
          <HR />
          {isRenault
            ? getFieldDecorator('segment', {
                ...(isSegmentLocked ? { initialValue: lockedSegment } : {}),
                rules: [
                  {
                    required: true,
                  },
                ],
              })(<input type="hidden" />)
            : (
              <FormItem label={t('maia.garageInfo.field.segment', 'Segment')} name="segment" hasFeedback>
                {getFieldDecorator('segment', {
                  ...(isSegmentLocked ? { initialValue: lockedSegment } : {}),
                  rules: [
                    {
                      required: true,
                    },
                  ],
                })(
                  <Select
                    placeholder={t('maia.garageInfo.placeholder.selectSegment', 'Select a segment')}
                    disabled={isSegmentLocked}
                    onChange={handleSegmentChange}
                  >
                    {segmentOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                )}
              </FormItem>
            )}
          {showTresorId && (
            <FormItem label={t('maia.garageInfo.field.tresorId', 'Tresor ID')} name="tresorId" hasFeedback>
              {getFieldDecorator('tresorId', {
                rules: [
                  {
                    required: false,
                  },
                  {
                    max: TRESOR_ID_MAX_LENGTH,
                    message: t('maia.garageInfo.validation.tresorIdMaxLength', 'Tresor ID must be at most {max} characters', {
                      max: TRESOR_ID_MAX_LENGTH,
                    }),
                  },
                ],
              })(
                <Input
                  disabled={tresorIdReadOnly}
                  maxLength={TRESOR_ID_MAX_LENGTH}
                  placeholder={
                    tresorIdReadOnly
                      ? t('maia.garageInfo.placeholder.tresorIdSynced', 'Synced from Kadensis')
                      : t('maia.garageInfo.placeholder.tresorId', 'Enter a Tresor ID')
                  }
                />
              )}
            </FormItem>
          )}
          <FormItem
            className="business-name-form-item"
            label={t('maia.garageInfo.field.businessName', 'Business name')}
            name="businessName"
            hasFeedback
            help={businessNameHelp}
          >
            {getFieldDecorator('businessName', {
              rules: [
                {
                  required: true,
                },
              ],
            })(<Input />)}
          </FormItem>
          <FormItem
            label={t('maia.garageInfo.field.legalName', 'Legal name')}
            name="legalName"
            hasFeedback
          >
            {getFieldDecorator('legalName', {
              rules: [
                {
                  required: false,
                },
                {
                  max: 255,
                  message: t(
                    'maia.garageInfo.validation.legalNameMaxLength',
                    'Legal name must be at most {max} characters',
                    { max: 255 }
                  ),
                },
              ],
            })(<Input maxLength={255} />)}
          </FormItem>
          {isExistingGarage && garage?.slug && (
            <GarageWebpageLink segment={garage.segment} slug={garage.slug} />
          )}
          <FormItem label={t('maia.garageInfo.field.email', 'Email')} name="email" hasFeedback>
            {getFieldDecorator('email', {
              rules: [
                {
                  required: true,
                },
              ],
            })(<Input />)}
          </FormItem>
          {!isMotrio && (
            <FormItem label={t('maia.garageInfo.field.website', 'Website')} name="website" hasFeedback>
              {getFieldDecorator('website', {
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </FormItem>
          )}
          <FormItem
            label={t('maia.garageInfo.field.phoneNumber', 'Phone number')}
            name="phoneNumber"
            hasFeedback
            help={phoneHelp}
          >
            {renderPhoneInput('phoneNumber')}
          </FormItem>
          {!isMotrio && (
            <FormItem
              className="google-place-id-form-item"
              label={t('maia.garageInfo.field.googlePlaceId', 'Google Place ID')}
              name="googlePlaceId"
              hasFeedback
              help={
                <span>
                  {t(
                    'maia.garageInfo.help.googlePlaceIdPrefix',
                    'This field is necessary for garages to show up on our directory pages. Find the place ID '
                  )}
                  <a
                    href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('maia.garageInfo.help.googlePlaceIdLink', 'here')}
                  </a>
                  {t('maia.garageInfo.help.googlePlaceIdSuffix', '.')}
                </span>
              }
            >
              {getFieldDecorator('googlePlaceId', {
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </FormItem>
          )}

          <LookupContainer>
            <AddressSelectorWithGooglePlaces
              countryRule={googlePlacesCountries}
              apiKey={`${config.gMaps.apiKey}`}
              disabled={!activeSegment}
              onAddressSelected={(a) => {
                setAddressValues(a);
              }}
            />
          </LookupContainer>

          <AddressContainer>
            <p>{t('maia.garageInfo.help.addressReadOnly', 'These fields are not editable. Please pick an address to update them.')}</p>
            <FormItem label={t('maia.garageInfo.field.addressLine1', 'Address line 1')} name="addressLine1" hasFeedback>
              {getFieldDecorator('addressLine1', {
                rules: [
                  {
                    required: true,
                  },
                ],
              })(<Input />)}
            </FormItem>
            <FormItem label={t('maia.garageInfo.field.addressLine2', 'Address line 2')} name="addressLine2" hasFeedback>
              {getFieldDecorator('addressLine2', {
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </FormItem>
            <FormItem label={t('maia.garageInfo.field.addressLine3', 'Address line 3')} name="addressLine3" hasFeedback>
              {getFieldDecorator('addressLine3', {
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </FormItem>
            <FormItem label={t('maia.garageInfo.field.city', 'City')} name="city" hasFeedback>
              {getFieldDecorator('city', {
                rules: [
                  {
                    required: true,
                  },
                ],
              })(<Input />)}
            </FormItem>
            <FormItem label={t('maia.garageInfo.field.county', 'County')} name="county" hasFeedback>
              {getFieldDecorator('county', {
                rules: [
                  {
                    required: true,
                  },
                ],
              })(<Input />)}
            </FormItem>
            <FormItem label={t('maia.garageInfo.field.postcode', 'Postcode')} name="postcode" hasFeedback>
              {getFieldDecorator('postcode', {
                rules: [
                  {
                    required: true,
                  },
                ],
              })(<Input />)}
            </FormItem>
            <FormItem label={t('maia.garageInfo.field.latitude', 'Latitude')} name="latitude" hasFeedback>
              {getFieldDecorator('latitude', {
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </FormItem>
            <FormItem label={t('maia.garageInfo.field.longitude', 'Longitude')} name="longitude" hasFeedback>
              {getFieldDecorator('longitude', {
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </FormItem>
          </AddressContainer>
          <FormItem label={t('maia.garageInfo.field.status', 'Status')} name="status" hasFeedback>
            {getFieldDecorator('status', {
              rules: [
                {
                  required: false,
                },
              ],
            })(
              <Select>
                <Option value="ACTIVE">{t('maia.garageInfo.status.active', 'Active')}</Option>
                {!isRenault && (
                  <Option value="SUSPENDED">{t('maia.garageInfo.status.suspended', 'Suspended')}</Option>
                )}
                <Option value="DELETED">{t('maia.garageInfo.status.deleted', 'Deleted')}</Option>
              </Select>
            )}
          </FormItem>
        </FormColumn>
        <FormColumn>
          <FormGroupTite>{t('maia.garageInfo.section.contactInformation', 'Contact information')}</FormGroupTite>
          <HR />
          <FormItem label={t('maia.garageInfo.field.contactJobTitle', 'Contact job title')} name="contactJobTitle" hasFeedback>
            {getFieldDecorator('contactJobTitle', {
              rules: [
                {
                  required: false,
                },
              ],
            })(<Input />)}
          </FormItem>
          <FormItem label={t('maia.garageInfo.field.contactFirstName', 'Contact first name')} name="contactFirstName" hasFeedback>
            {getFieldDecorator('contactFirstName', {
              rules: [
                {
                  required: true,
                },
              ],
            })(<Input />)}
          </FormItem>
          <FormItem label={t('maia.garageInfo.field.contactLastName', 'Contact last name')} name="contactLastName" hasFeedback>
            {getFieldDecorator('contactLastName', {
              rules: [
                {
                  required: false,
                },
              ],
            })(<Input />)}
          </FormItem>
          <FormItem
            label={t('maia.garageInfo.field.contactPhone', 'Contact phone')}
            name="contactPhone"
            hasFeedback
            help={contactHelp}
          >
            {renderPhoneInput('contactPhone')}
          </FormItem>
          {!isMotrio && (
            <FormItem
              label={t('maia.garageInfo.field.contactMobile', 'Contact mobile')}
              name="contactMobile"
              hasFeedback
              help={phoneHelp}
            >
              {renderPhoneInput('contactMobile', { mobileOnly: true })}
            </FormItem>
          )}
          {isMotrio &&  isExistingGarage &&(
            <>
              <FormGroupTite>
                {t('maia.garageInfo.section.marketingPreferences', 'Marketing preferences')}
              </FormGroupTite>
              <HR />
              <FormItem>
                <Checkbox disabled checked={garage?.marketingEmailPreference === 'ACCEPT' || garage?.marketingEmailPreference === 'PRESELECT'}>
                  {t('maia.garageInfo.checkbox.marketingPreferencesEmail', 'Email')}
                </Checkbox>
              </FormItem>
              <FormItem>
                <Checkbox disabled checked={garage?.marketingSmsPreference === 'ACCEPT' || garage?.marketingSmsPreference === 'PRESELECT'}>
                  {t('maia.garageInfo.checkbox.marketingPreferencesSMS', 'SMS')}
                </Checkbox>
              </FormItem>
            </>
          )}
          {!isMotrio && (
            <>
              <FormGroupTite>{t('maia.garageInfo.section.billingInformation', 'Billing information')}</FormGroupTite>
              <HR />
              <FormItem label={t('maia.garageInfo.field.xeroId', 'Xero ID')} name="xeroId" hasFeedback>
                {getFieldDecorator('xeroId', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Input placeholder={t('maia.garageInfo.placeholder.xeroId', 'Will be generated automatically')} disabled />)}
              </FormItem>
              <FormItem label={t('maia.garageInfo.field.vatNumber', 'VAT number')} name="vatNumber" hasFeedback>
                {getFieldDecorator('vatNumber', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Input />)}
              </FormItem>
              <FormItem label={t('maia.garageInfo.field.rcsNumber', 'RCS number (FR only)')} name="rcsNumber" hasFeedback>
                {getFieldDecorator('rcsNumber', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Input />)}
              </FormItem>
              <FormItem label={t('maia.garageInfo.field.siretNumber', 'SIRET number (FR only)')} name="siretNumber" hasFeedback>
                {getFieldDecorator('siretNumber', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Input />)}
              </FormItem>
              <FormItem label={t('maia.garageInfo.field.paymentAccountNumber', 'Payment account number')} name="paymentAccountNumber" hasFeedback>
                {getFieldDecorator('paymentAccountNumber', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Input />)}
              </FormItem>
              <FormItem label={t('maia.garageInfo.field.paymentSortCode', 'Payment sort code')} name="paymentSortCode" hasFeedback>
                {getFieldDecorator('paymentSortCode', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Input />)}
              </FormItem>
              <FormItem label={t('maia.garageInfo.field.accountsEmail', 'Accounts email')} name="accountsEmail" hasFeedback>
                {getFieldDecorator('accountsEmail', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Input />)}
              </FormItem>
            </>
          )}
          {!isMotrio && (
            <>
              <FormGroupTite>{t('maia.garageInfo.section.additionalDetails', 'Additional details')}</FormGroupTite>
              <HR />
              <FormItem name="garageIsBettaTester" hasFeedback>
                {getFieldDecorator('garageIsBettaTester', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Checkbox>{t('maia.garageInfo.checkbox.betaTester', 'Garage is beta tester')}</Checkbox>)}
              </FormItem>
              <FormItem name="isPremiumMember" hasFeedback>
                {getFieldDecorator('isPremiumMember', {
                  valuePropName: 'checked',
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(<Checkbox disabled={!canEditPremiumMembership}>{t('maia.garageInfo.checkbox.premiumMember', 'Garage is premium member')}</Checkbox>)}
              </FormItem>
            </>
          )}
        </FormColumn>
        <FormItem>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t('maia.garageInfo.save', 'Save')}
          </Button>
        </FormItem>
      </FormContainer>
    </Form>
  );
}

export default (mapPropsToFields) => Form.create({ name: 'garage_form', mapPropsToFields })(GarageForm);
